// File: components/HireMultiPeriodConfig.jsx
import React, { useState, useEffect } from 'react';
import HireConfigPeriod from './HireConfigPeriod';

const HireMultiPeriodConfig = ({
  periods = [],
  setPeriods,
  configOptions,
  updateSalaryCallback,
  maxPeriods = 4,
  hideEmploymentType = false,
  employmentType = 'employee',
  baseSalary = 0,
  formatCurrency,
  applyLocationMultiplier,
  multiPeriodConfigInfo,
  hireConfigPeriodInfo
}) => {
  useEffect(() => {
    if (!periods || !Array.isArray(periods) || periods.length === 0) {
      const defaultPeriod = {
        id: `period-${Date.now()}-default`,
        startMonth: 0,
        endMonth: 12,
        hoursPerWeek: 40,
        employmentType: employmentType
      };
      setPeriods([defaultPeriod]);
    }
  }, []);

  const generateId = () => `period-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const addPeriod = () => {
    if (periods.length >= maxPeriods) return;
    let latestEndMonth = 0;
    periods.forEach(period => {
      if (period.endMonth > latestEndMonth) {
        latestEndMonth = period.endMonth;
      }
    });
    if (latestEndMonth >= 12) return;

    const newPeriod = {
      id: generateId(),
      startMonth: latestEndMonth,
      endMonth: 12,
      hoursPerWeek: 40,
      employmentType: employmentType
    };
    const newPeriods = [...periods, newPeriod];
    setPeriods(newPeriods);
    updateSalaryCallback && updateSalaryCallback(newPeriods);
  };

  const updatePeriod = (updatedPeriod, hoursChanged = false) => {
    if (hideEmploymentType) {
      updatedPeriod.employmentType = employmentType;
    }

    const sortedPeriods = [...periods].sort((a, b) => a.startMonth - b.startMonth);
    const periodIndex = sortedPeriods.findIndex(p => p.id === updatedPeriod.id);
    const newPeriods = [...sortedPeriods];
    newPeriods[periodIndex] = updatedPeriod;

    if (periodIndex < newPeriods.length - 1) {
      for (let i = periodIndex + 1; i < newPeriods.length; i++) {
        const prevPeriod = newPeriods[i - 1];
        newPeriods[i].startMonth = prevPeriod.endMonth;
        if (newPeriods[i].startMonth >= newPeriods[i].endMonth) {
          newPeriods[i].endMonth = Math.min(12, newPeriods[i].startMonth + 1);
        }
      }
    }

    setPeriods(newPeriods);

    // Pass the hoursChanged flag to the callback so the parent component can
    // adjust the salary proportionally if needed
    updateSalaryCallback && updateSalaryCallback(newPeriods, hoursChanged);
  };

  const removePeriod = (periodId) => {
    if (periods.length === 1) return;
    const sortedPeriods = [...periods].sort((a, b) => a.startMonth - b.startMonth);
    const periodIndex = sortedPeriods.findIndex(p => p.id === periodId);
    if (periodIndex === -1) return;

    let newPeriods = [...sortedPeriods];

    if (periodIndex === 0 && newPeriods.length > 1) {
      newPeriods[1].startMonth = 0;
    } else if (periodIndex === newPeriods.length - 1) {
      newPeriods[periodIndex - 1].endMonth = 12;
    } else {
      newPeriods[periodIndex - 1].endMonth = newPeriods[periodIndex + 1].startMonth;
    }

    newPeriods = newPeriods.filter(p => p.id !== periodId);

    for (let i = 1; i < newPeriods.length; i++) {
      newPeriods[i].startMonth = newPeriods[i - 1].endMonth;
    }

    setPeriods(newPeriods);
    updateSalaryCallback && updateSalaryCallback(newPeriods);
  };

  const canAddPeriod = periods.length < maxPeriods && periods.some(p => p.endMonth < 12);

  return (
    <div className="mt-4">
      <h4 className="font-medium mb-3">{configOptions?.multiPeriodTitleLabel || multiPeriodConfigInfo?.multiPeriodTitleLabel || "Work Configuration Timeline"}</h4>

      {/* Timeline Bar */}
      <div className="mb-4">
        <div className="relative h-16 bg-gray-200 rounded-lg overflow-hidden">
          {periods.map((period, index) => {
            const monthsInPeriod = period.endMonth - period.startMonth;
            const widthPct = (monthsInPeriod / 12) * 100;
            const leftPct = (period.startMonth / 12) * 100;
            const isContractor = hideEmploymentType ? employmentType === 'contractor' : period.employmentType === 'contractor';

            const baseCost = baseSalary * (period.hoursPerWeek / 40) * (monthsInPeriod / 12) * (isContractor ? 1.5 : 1);
            const periodCost = applyLocationMultiplier ? applyLocationMultiplier(baseCost) : baseCost;

            const color = isContractor
              ? ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500']
              : ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500'];

            return (
              <div
                key={period.id}
                className={`absolute h-full flex flex-col items-center justify-center text-white text-xs font-medium ${color[index % color.length]} border-r-2 border-white`}
                style={{ width: `${widthPct}%`, left: `${leftPct}%` }}
                title={`${configOptions?.monthsLabel || multiPeriodConfigInfo?.monthsLabel || "Months"} ${period.startMonth}-${period.endMonth}: ${period.hoursPerWeek} ${multiPeriodConfigInfo?.hrsWeekShortText || "hrs/week"}, ${configOptions?.costLabel || multiPeriodConfigInfo?.costLabel || "Cost"}: ${formatCurrency ? formatCurrency(periodCost) : '$' + periodCost.toFixed(0)}`}
              >
                <span className="truncate px-1 font-bold">{monthsInPeriod} {multiPeriodConfigInfo?.mosShortText || "mos"}</span>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{multiPeriodConfigInfo?.monthLabels?.[0] || "0"}</span>
          <span>{multiPeriodConfigInfo?.monthLabels?.[1] || "3"}</span>
          <span>{multiPeriodConfigInfo?.monthLabels?.[2] || "6"}</span>
          <span>{multiPeriodConfigInfo?.monthLabels?.[3] || "9"}</span>
          <span>{multiPeriodConfigInfo?.monthLabels?.[4] || "12"}</span>
        </div>

        <div className="mt-3 space-y-1">
          {periods.map((period, index) => {
            const monthsInPeriod = period.endMonth - period.startMonth;
            const isContractor = hideEmploymentType ? employmentType === 'contractor' : period.employmentType === 'contractor';
            const baseCost = baseSalary * (period.hoursPerWeek / 40) * (monthsInPeriod / 12) * (isContractor ? 1.5 : 1);
            const cost = applyLocationMultiplier ? applyLocationMultiplier(baseCost) : baseCost;

            const color = isContractor
              ? ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500']
              : ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500'];

            return (
              <div key={period.id} className="flex items-center gap-2 text-sm">
                <div className={`w-3 h-3 ${color[index % color.length]} rounded`} />
                <span className="flex-1">
                  {configOptions?.monthsLabel || multiPeriodConfigInfo?.monthsLabel || "Months"} {period.startMonth}-{period.endMonth} ({period.hoursPerWeek}{multiPeriodConfigInfo?.hrsWeekShortText || "h/wk"}, {isContractor ? configOptions?.contractorLabel || 'Contractor' : configOptions?.employeeLabel || 'Employee'})
                </span>
                <span className="font-medium">{formatCurrency ? formatCurrency(cost) : `$${cost.toFixed(0)}`}</span>
              </div>
            );
          })}

          <div className="border-t pt-1 flex justify-between font-bold">
            <span>{multiPeriodConfigInfo?.totalAnnualCostLabel || "Total Annual Cost:"}</span>
            <span>
              {formatCurrency ? formatCurrency(
                periods.reduce((total, period) => {
                  const monthsInPeriod = period.endMonth - period.startMonth;
                  const isContractor = hideEmploymentType ? employmentType === 'contractor' : period.employmentType === 'contractor';
                  const baseCost = baseSalary * (period.hoursPerWeek / 40) * (monthsInPeriod / 12) * (isContractor ? 1.5 : 1);
                  return total + (applyLocationMultiplier ? applyLocationMultiplier(baseCost) : baseCost);
                }, 0)
              ) : '$0'}
            </span>
          </div>
        </div>
      </div>

      {/* Period Editors */}
      {periods.map((period, index) => (
        <HireConfigPeriod
          key={period.id}
          period={period}
          updatePeriod={updatePeriod}
          removePeriod={() => removePeriod(period.id)}
          isLastPeriod={index === periods.length - 1}
          isFirstPeriod={index === 0}
          configOptions={configOptions}
          maxPeriods={maxPeriods}
          hideEmploymentType={hideEmploymentType}
          hireConfigPeriodInfo={hireConfigPeriodInfo}
        />
      ))}

      {/* Add Period Button */}
      {canAddPeriod && (
        <div className="mt-2">
          <button
            onClick={addPeriod}
            className="text-blue-500 hover:text-blue-700 text-sm"
          >
            {configOptions?.addPeriodLabel || multiPeriodConfigInfo?.addPeriodLabel || "+ Add Another Period"}
          </button>
          <span className="text-xs text-gray-500 ml-2">
            {configOptions?.maxPeriodsNoteLabel?.replace('{current}', periods.length).replace('{max}', maxPeriods) ||
             multiPeriodConfigInfo?.maxPeriodsNoteLabel?.replace('{current}', periods.length).replace('{max}', maxPeriods) ||
             `(${periods.length}/${maxPeriods} periods)`}
          </span>
        </div>
      )}

      <div className="text-xs text-gray-600 mt-3">
        {configOptions?.periodHelpText || multiPeriodConfigInfo?.periodHelpText || "Use multiple periods to define different working arrangements throughout the year."}
      </div>
    </div>
  );
};

export default HireMultiPeriodConfig;
